import React, { useEffect, useMemo, useState } from 'react';
import { getter } from './utils';
import { formatEntityDataField, getCaseInsensitiveValue } from 'shared/utils';
import moment from 'moment-timezone';
import { Box, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography } from '@mui/material';
import SelectMUI from 'components/mui/select/select.component';
import Carousel from 'react-material-ui-carousel';
import { ArrowBackIcon, ArrowForwardIcon, CarrotDownIcon, CarrotUpIcon, InformationIcon } from 'components/mui';
import { Icon } from 'components/icon/icon.component';
import CircularProgress from '@mui/material/CircularProgress';

// Fields surfaced in the header card — excluded from the expanded "Additional Details" section
const headerColumns = ['name', 'breed', 'species'];
function renderNullable(value) {
  return value !== null && value !== undefined && value !== '' ? value : <Typography variant="nullData" color="#BEBEBE">
      <span>--</span>
    </Typography>;
}
function renderFieldValue(field, value) {
  if (value === null || value === undefined || value === '') return null;
  if (field.type === 'datetime') return moment(value).local().format('MMM D, YYYY h:mm A');
  if (field.type === 'date') return moment(value).format('MMM D, YYYY');
  return value;
}
function SectionHeader({
  icon,
  title,
  expanded,
  onToggle
}) {
  return <Box onClick={onToggle} sx={{
    'display': 'flex',
    'alignItems': 'center',
    'justifyContent': 'space-between',
    'px': '16px',
    'py': '10px',
    'cursor': 'pointer',
    'borderTop': '1px solid #EDEFF1',
    'backgroundColor': expanded ? '#F3F6FC' : '#FAFAFA',
    ':hover': {
      backgroundColor: '#F3F6FC'
    }
  }}>
      <Stack direction="row" spacing="10px" sx={{
      alignItems: 'center'
    }}>
        {icon}
        <Typography variant="tableHeader">{title}</Typography>
      </Stack>
      {expanded ? <CarrotUpIcon /> : <CarrotDownIcon />}
    </Box>;
}
export function AnimalsComponent(props) {
  const {
    animals,
    availableModelsFields,
    availableModels,
    _detailsTabs,
    handleDetailsTab,
    selectedDetailsTab,
    isLoading,
    selectedAnimal
  } = props;
  const [selectedAnimalIndex, setSelectedAnimalIndex] = useState(0);
  const [detailsExpanded, setDetailsExpanded] = useState(true);
  const animalList = useMemo(() => animals || [], [animals]);
  useEffect(() => {
    if (!selectedAnimal || !animalList.length) return;
    const index = animalList.findIndex(a => a.id === selectedAnimal);
    if (index > -1) setSelectedAnimalIndex(index);
  }, [selectedAnimal, animalList]);
  const allAnimalFields = availableModelsFields?.find(model => model.id === 'animal')?.fields || [];
  const additionalFields = allAnimalFields.filter(f => !headerColumns.includes(f.id));
  return <>
      <Stack sx={{
      'height': '100%',
      'background': '#E4E9F1',
      'borderBottomLeftRadius': '28px',
      'borderBottomRightRadius': '28px',
      'overflowY': 'scroll',
      'scrollbarWidth': 'thin',
      '&::-webkit-scrollbar': {
        width: '0.4em'
      },
      '&::-webkit-scrollbar-track': {
        background: '#f1f1f1',
        marginTop: '25px',
        marginBottom: '25px'
      },
      '&::-webkit-scrollbar-thumb': {
        backgroundColor: '#888'
      },
      '&::-webkit-scrollbar-thumb:hover': {
        background: '#555'
      }
    }} className="d-flex h-100" spacing="14px">
        <Box className="d-flex w-100 justify-content-between" sx={{
        position: 'relative',
        pt: '14px',
        pb: '38px',
        display: 'flex'
      }}>
          <span />
          <Box sx={{
          position: 'absolute',
          left: '37.5%'
        }}>
            <SelectMUI height="36px" width="180px" top="6px" zIndex={9001} options={_detailsTabs} value={selectedDetailsTab || ''} onChange={opt => handleDetailsTab(opt.key)} />
          </Box>
          {animalList?.length > 0 && <Box sx={{
          position: 'absolute',
          left: '77%',
          top: '24px'
        }}>
              {'' + (selectedAnimalIndex + 1) + ' of ' + animalList.length + ' Items'}
            </Box>}
        </Box>

        <Stack>
          {isLoading ? <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          pt: '25px'
        }}>
              <CircularProgress />
            </Box> : <>
              {animalList?.length > 0 ? <Carousel index={selectedAnimalIndex} navButtonsAlwaysVisible cycleNavigation={false} autoPlay={false} animation="slide" indicators={false} PrevIcon={<ArrowBackIcon fill="#fff" />} NextIcon={<ArrowForwardIcon fill="#fff" />} onChange={now => setSelectedAnimalIndex(now)}>
                  {animalList.map((animal, i) => <Box key={animal.id || i} sx={{
              px: '50px'
            }}>
                      <Paper sx={{
                'borderRadius': '10px',
                'overflowY': 'scroll',
                'scrollbarWidth': 'thin',
                '&::-webkit-scrollbar': {
                  width: '0.4em'
                },
                '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
                  marginTop: '10px',
                  marginBottom: '10px'
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#888'
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: '#555'
                },
                'height': '68vh'
              }}>
                        {/* ── Section 1: Header (Company Animal Data) ── */}
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Stack spacing="10px" direction="row" sx={{
                          alignItems: 'center'
                        }}>
                                  <Icon name="custom-pets" size={24} />
                                  <Typography variant="tableHeader">Animal</Typography>
                                </Stack>
                              </TableCell>
                              <TableCell>{renderNullable(animal.name)}</TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>
                                <Typography variant="tableHeader">Breed</Typography>
                              </TableCell>
                              <TableCell>{renderNullable([animal.color, animal.breed].filter(Boolean).join(' ') || null)}</TableCell>
                            </TableRow>

                            <TableRow>
                              <TableCell>
                                <Typography variant="tableHeader">Species</Typography>
                              </TableCell>
                              <TableCell>{renderNullable(animal.species)}</TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>

                        {/* ── Section 2: Additional Details ── */}
                        {availableModels.indexOf('animal') > -1 && additionalFields.length > 0 && <>
                            <SectionHeader icon={<InformationIcon />} title="Additional Details" expanded={detailsExpanded} onToggle={() => setDetailsExpanded(!detailsExpanded)} />
                            {detailsExpanded && <Table>
                                <TableBody>
                                  {additionalFields.map((field, index) => {
                        const fieldValue = animal[field.id];
                        const rendered = renderFieldValue(field, fieldValue);
                        if (rendered !== null && rendered !== undefined) {
                          return <TableRow key={field.id + index}>
                                          <TableCell>
                                            <Typography variant="tableHeader">{field.name}</Typography>
                                          </TableCell>
                                          <TableCell>{rendered}</TableCell>
                                        </TableRow>;
                        }
                        if (field.id.startsWith('entity_data__')) {
                          return <TableRow key={field.id + index}>
                                          <TableCell>
                                            <Typography variant="tableHeader">{field.name}</Typography>
                                          </TableCell>
                                          <TableCell>
                                            {(() => {
                                const keys = field.id.split('__');
                                const rawValue = keys.length === 2 ? getCaseInsensitiveValue(animal.entity_data, keys[1]) : getter(animal, keys);
                                const formatted = formatEntityDataField(field, rawValue);
                                return formatted !== null && formatted !== undefined ? formatted : <Typography sx={{
                                  color: '#BEBEBE'
                                }}>--</Typography>;
                              })()}
                                          </TableCell>
                                        </TableRow>;
                        }
                        return <React.Fragment key={field.id} />;
                      })}
                                </TableBody>
                              </Table>}
                          </>}
                      </Paper>
                    </Box>)}
                </Carousel> : <Stack>
                  <Box sx={{
              display: 'flex',
              alignItems: 'center',
              alignContent: 'center',
              justifyContent: 'center',
              height: '100%',
              width: '100%'
            }}>
                    <Typography>Customer has no animals</Typography>
                  </Box>
                </Stack>}
            </>}
        </Stack>
      </Stack>
    </>;
}